import { isTechnologyRequired, TECHNOLOGY_REQUIRED_POSITION_IDS } from 'domain/shared/career';
import type { PositionSlotFormValues, SecondaryCompetencyFormValues } from '../types';

const technologyRequiredIds = TECHNOLOGY_REQUIRED_POSITION_IDS.flatMap(positionId => [positionId, String(positionId)]);

const technologyRequiredCondition = { value: true, when: { $specializationId: { in: technologyRequiredIds } } };

// `$.` resolves against the row; a plain `$` would read the primary specialization.
const rowTechnologyRequiredCondition = { value: true, when: { '$.specializationId': { in: technologyRequiredIds } } };

const secondaryCompetencySchema = {
  type: 'object',
  _divisible: false,
  properties: {
    specializationId: { type: 'string', required: true },
    technologyId: { type: 'string', required: rowTechnologyRequiredCondition },
    competencyLevel: { type: 'string', required: true },
  },
};

const positionSlotValidationSchema = {
  specializationId: { type: 'string', required: true },
  technologyId: { type: 'string', required: technologyRequiredCondition },
  competencyLevel: { type: 'string', required: true },
  secondaryCompetencies: { type: 'array', forEach: secondaryCompetencySchema },
  officeIds: { type: 'array', required: true, minSize: 1 },
  workerType: { type: 'string', required: true },
  workRateType: { type: 'string', required: true },
  businessFunctionId: { type: 'string', required: true },
  // TODO: Restore required: true before the official production release (temporary UI fix).
  positionProfileId: { type: 'string', required: false },
};

export const getPositionSlotValidationSchema = () => Promise.resolve(positionSlotValidationSchema);

const pairKey = ({
  specializationId,
  technologyId,
}: Pick<SecondaryCompetencyFormValues, 'specializationId' | 'technologyId'>) =>
  `${Number(specializationId)}:${isTechnologyRequired(specializationId) ? technologyId || '' : ''}`;

export const DUPLICATE_SECONDARY_COMPETENCY_MESSAGE =
  'Secondary competencies must be unique and must differ from the primary specialization and technology.';

const isPairComplete = ({ specializationId, technologyId }: SecondaryCompetencyFormValues) =>
  specializationId != null &&
  specializationId !== '' &&
  (!isTechnologyRequired(specializationId) || Boolean(technologyId));

// True when the candidate row repeats the primary pair or another row; incomplete pairs are never duplicates.
export const isDuplicateSecondaryCompetency = (
  primary: Pick<PositionSlotFormValues, 'specializationId' | 'technologyId'>,
  rows: SecondaryCompetencyFormValues[],
  index: number,
  candidate: SecondaryCompetencyFormValues,
): boolean => {
  if (!isPairComplete(candidate)) return false;
  const key = pairKey(candidate);
  return (
    key === pairKey(primary) ||
    rows.some((row, rowIndex) => rowIndex !== index && isPairComplete(row) && pairKey(row) === key)
  );
};

// Cross-row rules the declarative schema cannot express; the backend enforces them as well.
export const validateSecondaryCompetencyPairs = ({
  specializationId,
  technologyId,
  secondaryCompetencies,
}: Pick<PositionSlotFormValues, 'specializationId' | 'technologyId' | 'secondaryCompetencies'>): string | null => {
  const seen = new Set([pairKey({ specializationId, technologyId })]);

  for (const competency of secondaryCompetencies ?? []) {
    const key = pairKey(competency);
    if (seen.has(key)) {
      return DUPLICATE_SECONDARY_COMPETENCY_MESSAGE;
    }
    seen.add(key);
  }

  return null;
};
